import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
  Flex,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableCell,
  TextInput,
  StretchPanel,
} from "@sopranodesign/pulse-components";

const UserDetails = ({ users }) => {
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Client-side filtering and sorting
  const filteredAndSortedUsers = useMemo(() => {
    if (!users || users.length === 0) return [];

    let result = [...users];

    // Filter by search text (name or email)
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter(
        (user) =>
          `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(search) || user.email?.toLowerCase().includes(search),
      );
    }

    // Sort by field
    if (sortField) {
      result.sort((a, b) => {
        let aVal, bVal;

        switch (sortField) {
          case "name":
            aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
            bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
            break;
          case "email":
            aVal = a.email?.toLowerCase() || "";
            bVal = b.email?.toLowerCase() || "";
            break;
          case "created_at":
            aVal = new Date(a.created_at).getTime();
            bVal = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [users, searchText, sortField, sortDirection]);

  const handleSort = (field) => {
    if (!field) return;

    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortableHeader = ({ label, field }) => (
    <TableHeadCell
      style={{ cursor: field ? "pointer" : "default" }}
      onClick={() => field && handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span style={{ marginLeft: "4px" }}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
    </TableHeadCell>
  );

  return (
    <StretchPanel>
      <Flex mb style={{ padding: "16px 16px 0" }}>
        <TextInput
          placeholder="Search by name or email"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: "100%" }}
        />
      </Flex>
      <Table loadingColumns={3}>
        <TableHead>
          <TableRow>
            <SortableHeader label="Name" field="name" />
            <SortableHeader label="Email" field="email" />
            <SortableHeader label="Created At" field="created_at" />
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedUsers.map(
            ({ first_name, last_name, email, created_at }) => (
              <TableRow key={`${first_name}${last_name}${email}`}>
                <TableCell>{`${first_name} ${last_name}`}</TableCell>
                <TableCell>{email}</TableCell>
                <TableCell>{dayjs(created_at).format("DD/MM/YYYY")}</TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      {filteredAndSortedUsers.length === 0 && searchText && (
        <Flex center style={{ padding: "20px" }}>
          <p style={{ color: "#666" }}>
            No users found matching "{searchText}"
          </p>
        </Flex>
      )}
    </StretchPanel>
  );
};

UserDetails.propTypes = {
  users: PropTypes.any,
};

export default UserDetails;
